import { type Installment, type VMModel } from '@hooks/use-vm-model'
//import { EmploymentData } from '@schemas/employment'

import { getNumberOfSteps } from '@utils/steps'
import * as yup from 'yup'
import { type Modals } from './modal'
import { type RichiestaInsert, type State } from '@store/defaultReducerState.ts'
import type { EmploymentData } from '@src/schemas/employment'

export const Steps = {
	LANDING: 0,
	CONTATTI: 1,
	DATI_ANAGRAFICI: 2,
	PROFESSIONE: 3,
	SERVIZI_FACOLTATIVI: 4,
	RIEPILOGO: 5,
} as const

export type Step = (typeof Steps)[keyof typeof Steps]

/* 
enum Steps {
	PREVENTIVO,
	RICHIESTA,
	ANAGRAFICA,
	DATI,
	SERVIZI,
	RIEPILOGO,
	MOBILE_UPLOAD,
	THANK_YOU,
}
 */
enum Actions {
	UpdateServizi,
	MoveToStep,
	UpdateLoan,
	UpdateAppError,
	UpdateUiError,
	UpdateDefaults,
	UpdateFetchingSources,
	UpdateInstallments,
	UpdateOPTCode,
	UpdateData,
	UpdateSondaggio,
	UpdateUserCFInfos,
	UpdateValidationSchema,
	UpdateInsurance,
	UpdateWorkInfos,
	UpdateServiceSurvey,
	UpdateServiceHeir,
	UpdateServiceThirdParty,
	ShowUIProgressBar,
	ShowUIOTP,
	ShowModal,
	UpdateRichiesta,
	UpdateCocalFin,
	UpdateFiles,
	UpdateMobileToken,
	SetThanksDone,
	UpdateOTP,
	UpdateOCS,
	SetCIE,
	UpdateIP,
	setStepInvisible,
	UpdateDataToken,
	isModify,
	isLanding,
	setReady,
	UpdateTestData,
	UpdateBlockSond,
	UpdateIdentinet,
	UpdateContractDuration,
	UpdateServiceBenEscCom,
	UpdateIsSecci,
	SetIdRichiesta,
	SetQrDisclaimer
}

type FetchingSourceAction = 'ADD' | 'REMOVE'

type AppActions =
	| { type: Actions.UpdateDefaults; payload?: Partial<State['defaults']> }
	| { type: Actions.MoveToStep; payload?: Step }
	| { type: Actions.UpdateLoan; payload: State['data'] }
	| { type: Actions.UpdateInstallments; payload: Installment[] }
	| {
			type: Actions.UpdateDataToken
			payload: Partial<State>
	  }
	| {
			type: Actions.UpdateTestData
			payload: Partial<State['data']['One']> // payload può contenere solo alcune proprietà di One
	  }
	| {
			type: Actions.UpdateData
			payload: Omit<Partial<State['data']>, 'Thanks'>
	  }
	| {
			type: Actions.UpdateSondaggio
	  }
	| {
			type: typeof Actions.UpdateIP
			payload: string // oppure { ip: string } se vuoi usare un oggetto
	  }
	| {
			type: typeof Actions.setStepInvisible
			payload: boolean
	  }
	| {
			type: typeof Actions.isModify
			payload: boolean
	  }
	| {
			type: typeof Actions.isLanding
			payload: boolean
	  }
	| {
			type: typeof Actions.UpdateIsSecci
			payload: boolean
	  }
	| {
			type: typeof Actions.setReady
			payload: boolean
	  }
	| {
			type: typeof Actions.UpdateBlockSond
			payload: boolean
	  }
	| {
			type: Actions.UpdateWorkInfos
			payload: Pick<
				EmploymentData['CompanyAddress'],
				'Provincia' | 'Comune' | 'ProvinciaLabel' | 'ComuneLabel'
			>
	  }
	| { type: Actions.UpdateOPTCode; payload: string }
	| { type: Actions.UpdateFetchingSources; payload: FetchingSourceAction }
	| { type: Actions.UpdateAppError; payload: boolean }
	| {
			type: Actions.UpdateUserCFInfos
			payload: Partial<
				Pick<
					VMModel['Two'],
					| 'Gender'
					| 'BirthDate'
					| 'BirthLocation'
					| 'BirthState'
					| 'CellNumber'
					| 'BirthLocationId'
					| 'BirthStateId'
					| 'FiscalCode'
				>
			>
	  }
	| {
			type: Actions.UpdateValidationSchema
			payload: yup.ObjectSchema<yup.AnyObject>
	  }
	| { type: Actions.UpdateUiError; payload: boolean }
	| { type: Actions.UpdateServiceSurvey; payload: boolean }
	| { type: Actions.UpdateServiceHeir; payload: boolean }
	| { type: Actions.UpdateServiceBenEscCom; payload: boolean }
	| { type: Actions.UpdateServiceThirdParty; payload: boolean }
	| { type: Actions.UpdateInsurance; payload: boolean }
	| {
			type: Actions.UpdateIdentinet
			payload: { show: boolean; duration: string }
	  }
	| {
			type: Actions.UpdateContractDuration
			payload: string
	  }
	| { type: Actions.ShowUIOTP; payload: boolean }
	| { type: Actions.ShowUIProgressBar; payload: boolean }
	| { type: Actions.ShowModal; payload: Modals }
	| { type: Actions.UpdateRichiesta; payload: RichiestaInsert }
	| { type: Actions.SetIdRichiesta; payload: number }
| {
		type: Actions.SetQrDisclaimer
		payload: {
			disclaimer: string
			importo: string
			rate?: Partial<Installment>
			descrMotivazione:string
			insurance :boolean

		}
  }

	| {
			type: Actions.UpdateCocalFin
			payload: {
				CocalfinID: number
				FinancialTable: string
				Prodotto: string
				TipoProdotto: string
				Emettitore: string
				Provenienza: string
			}
	  }
	| {
			type: Actions.UpdateFiles
			payload: {
				files: Array<File>
				name: keyof State['files']
			}
	  }
	| { type: Actions.UpdateMobileToken; payload: string }
	| { type: Actions.SetThanksDone; payload: boolean }
	| { type: Actions.SetCIE; payload: boolean }
	| { type: Actions.UpdateOTP; payload: boolean }
	| { type: Actions.UpdateServizi; payload: Partial<State['servizi']> }
	| {
			type: Actions.UpdateOCS
			payload: {
				NumeroPratica: number
				Controparte: number
			}
	  }

function reducer(state: State, action: AppActions): State {
	//console.log('Payload in dispatch:', action.payload);

	switch (action.type) {
		case Actions.setStepInvisible:
			return {
				...state,
				stepInvisible: action.payload,
			}
		case Actions.isLanding:
			return {
				...state,
				isLanding: action.payload,
			}

		case Actions.UpdateIsSecci:
			return {
				...state,
				data: {
					...state.data,
					One: {
						...state.data.One,
						IsSecciVisible: action.payload,
					},
				},
			}
		case Actions.isModify:
			return {
				...state,
				isModify: action.payload,
			}
		case Actions.setReady:
			return {
				...state,
				isReady: action.payload,
			}
		case Actions.UpdateBlockSond:
			return {
				...state,
				blockedSelectedSond: action.payload,
			}

		case Actions.SetCIE:
			return {
				...state,
				ui: {
					...state.ui,
					isCIE: action.payload,
				},
			}
		case Actions.UpdateOCS:
			return {
				...state,
				ocs: {
					NumeroPratica: action.payload.NumeroPratica,
					Controparte: action.payload.Controparte,
				},
			}
		case Actions.UpdateCocalFin:
			return {
				...state,
				data: {
					...state.data,
					One: {
						...state.data.One,
						...action.payload,
					},
				},
			}
		case Actions.UpdateOTP:
			return {
				...state,
				data: {
					...state.data,
					verifyOTP: action.payload,
				},
			}
		case Actions.SetThanksDone:
			return {
				...state,
				data: {
					...state.data,
					Thanks: {
						done: action.payload,
					},
				},
			}
		case Actions.UpdateMobileToken:
			return {
				...state,
				mobileToken: state.mobileToken || action.payload, //FIX
			}
		case Actions.UpdateFiles:
			return {
				...state,
				files: {
					...state.files,
					[action.payload.name]: action.payload.files,
				},
			}
		case Actions.UpdateRichiesta:
			return {
				...state,
				ui: {
					...state.ui,
					richiesta: action.payload,
				},
			}

		case Actions.SetIdRichiesta: {
			return {
				...state,
				data: {
					...state.data,
					IdRichiesta: action.payload,
				},
			}
		}
			
case Actions.SetQrDisclaimer: {

	
	return {
	...state,
	data: {
		...state.data,

		Disclaimer: action.payload.disclaimer,

		One: {
			...state.data.One,

			Amount: action.payload.importo,

			SelectedInstallment: action.payload.insurance
				? state.data.One.SelectedInstallment
				: {
						...state.data.One.SelectedInstallment,
						...(action.payload.rate ?? {}),
					},

			Insurance: action.payload.insurance,

			DescrMotivazione: action.payload.descrMotivazione,
		},

		Seven: {
			...state.data.Seven,

			SelectedInstallmentSond: action.payload.insurance
				? {
						...state.data.Seven.SelectedInstallmentSond,
						...(action.payload.rate ?? {}),
					}
				: state.data.Seven.SelectedInstallmentSond,
		},
	},
}
}


		case Actions.UpdateServizi: {
			return {
				...state,
				servizi: {
					...state.servizi,
					...action.payload,
				},
			}
		}

		case Actions.MoveToStep: {
			// se il payload è presente, passo a quello step; altrimenti se sono all'ultimo step, rimango fermo, oppure passo allo step successivo
			const nextStep =
				state.currentStep < getNumberOfSteps()
					? state.currentStep + 1
					: state.currentStep

			return {
				...state,

				currentStep: action.payload !== undefined ? action.payload : nextStep,
				errors: {
					ui: false,
					app: false,
					modal: false,
				},
				ui: {
					...state.ui,
					otp: false,
					progressBar: true,
					modal: null,
				},
				validationSchema: new yup.ObjectSchema({}), // reset dello schema al cambio di step
			}
		}
		case Actions.UpdateDataToken:
			return {
				...state,
				...action.payload,
			}

		case Actions.UpdateTestData:
			return {
				...state,
				data: {
					...state.data,
					One: {
						...state.data.One,
						...action.payload,
					},
				},
			}
		case Actions.UpdateData:
			return {
				...state,
				data: {
					...state.data,
					...action.payload,
				},
			}
		//ADD 4.3 when flags go false must be reset array
		case Actions.UpdateSondaggio:
			return {
				...state,
				data: {
					...state.data,
					Seven: {
						...state.data.Seven,
						Sondaggio: [], // resetti l’array a vuoto
					},
				},
			}

		case Actions.UpdateIP:
			return {
				...state,
				data: {
					...state.data,
					Two: {
						...state.data.Two,
						IP: action.payload,
					},
				},
			}

		case Actions.UpdateInstallments:
			return {
				...state,
				data: {
					...state.data,
					One: {
						...state.data.One,
						Installments: action.payload,
					},
				},
			}
		case Actions.UpdateUserCFInfos:
			return {
				...state,
				data: {
					...state.data,
					Two: {
						...state.data.Two,
						...action.payload,
					},
				},
			}
		case Actions.UpdateWorkInfos:
			return {
				...state,
				data: {
					...state.data,
					Four: {
						...state.data.Four,
						CompanyAddress: {
							...state.data.Four.CompanyAddress,
							...action.payload,
						},
					},
				},
			}
		case Actions.UpdateLoan:
			return {
				...state,
				data: {
					...state.data,
					...action.payload,
				},
			}
		case Actions.UpdateAppError:
			return {
				...state,
				errors: {
					...state.errors,
					app: action.payload,
				},
			}
		case Actions.UpdateUiError:
			return {
				...state,
				errors: {
					...state.errors,
					ui: action.payload,
				},
			}

		case Actions.UpdateDefaults:
			return {
				...state,
				defaults: {
					...state.defaults,
					...action.payload,
				},
			}
		case Actions.UpdateFetchingSources:
			return {
				...state,
				fetching: {
					sources: state.fetching.sources + (action.payload === 'ADD' ? 1 : -1),
					done:
						state.fetching.sources + (action.payload === 'ADD' ? 1 : -1) === 0,
				},
			}
		case Actions.ShowUIOTP:
			return {
				...state,
				ui: {
					...state.ui,
					otp: action.payload,
				},
			}
		case Actions.ShowUIProgressBar:
			return {
				...state,
				ui: {
					...state.ui,
					progressBar: action.payload,
				},
			}
		case Actions.UpdateOPTCode:
			return {
				...state,
				otpCode: action.payload,
			}
		case Actions.ShowModal:
			return {
				...state,
				ui: {
					...state.ui,
					modal: action.payload,
				},
			}
		case Actions.UpdateValidationSchema:
			return {
				...state,
				validationSchema: action.payload,
			}
		case Actions.UpdateInsurance:
			return {
				...state,
				data: {
					...state.data,
					One: {
						...state.data.One,
						Insurance: action.payload,
					},
					...(action.payload === false && {
						Seven: {
							...state.data.Seven,
							SelectedInstallmentSond: {
								InstallmentsCount: 0,
								Amount: 0,
								Tan: 0,
								Taeg: 0,
								PrgSimul: 0,
								Erogato: 0,
								FinanziamLordo: 0,
								ImpServiziFinanz: 0,
								ImpServiziIdentinet: 0,
							},
						},
					}),
				}
			}

		case Actions.UpdateIdentinet:
			return {
				...state,
				servizi: {
					...state.servizi,
					identinetActive: action.payload.show,
					identinetContractDuration: action.payload.duration,
				},
			}

		case Actions.UpdateContractDuration:
			return {
				...state,
				servizi: {
					...state.servizi,
					identinetContractDuration: action.payload,
				},
			}
		case Actions.UpdateServiceSurvey:
			return {
				...state,
				servizi: {
					...state.servizi,
					validSurvey: action.payload,
				},
			}
		case Actions.UpdateServiceHeir:
			return {
				...state,
				servizi: {
					...state.servizi,
					heir: action.payload,
				},
			}

		case Actions.UpdateServiceBenEscCom:
			return {
				...state,
				servizi: {
					...state.servizi,
					beneficiaryEscComm: action.payload,
				},
			}
		case Actions.UpdateServiceThirdParty:
			return {
				...state,
				servizi: {
					...state.servizi,
					thirdParty: action.payload,
				},
			}
		default:
			return state
	}
}

export type { AppActions, FetchingSourceAction }
export { reducer, Actions }
